import { range } from "@prismicio/editor-support/Array"
import { keyBy } from "@prismicio/editor-support/Map"
import {
  BlankSlate,
  BlankSlateDescription,
  BlankSlateIcon,
  BlankSlateTitle,
  Box,
  ComboBoxItem,
  Icon,
  ProgressCircle,
  ScrollArea,
  Separator,
  Skeleton,
  Text
} from "@prismicio/editor-ui"
import { type Dispatch, type SetStateAction, useMemo } from "react"
import { Link, useNavigate } from "react-router-dom"

import { useRepositoryTypes } from "../../app/services/customTypes"
import { trackSelectDocumentInDialog } from "../../app/services/tracking"
import {
  useRepositoryTagsFilters,
  useRepositoryTypesFilters
} from "../DocumentListBody/ActionBar/ActionBar"
import {
  FilterDropDown,
  FilterDropDownBoundary
} from "../DocumentListBody/ActionBar/FilterDropdown"
import { FiltersBar } from "../DocumentListBody/ActionBar/FiltersBar"
import type {
  Filter,
  FilterType
} from "../DocumentListBody/ActionBar/useActionBar"
import { useSearchDialogContext } from "./useDocumentSearch"

interface DocumentSearchContentProps {
  selectedFilters: Filter[]
  setSelectedFilters: Dispatch<SetStateAction<Filter[]>>
}

export function DocumentSearchContent(props: DocumentSearchContentProps) {
  const { selectedFilters, setSelectedFilters } = props
  const { setLoading } = useSearchDialogContext()
  return (
    <>
      <ActionBar
        selectedFilters={selectedFilters}
        setSelectedFilters={(filters) => {
          setLoading(true)
          setSelectedFilters(filters)
        }}
      />
      <Separator />

      <Box flexDirection="column" overflowY="auto" height="350px">
        <DocumentList />
      </Box>
    </>
  )
}

function DocumentList() {
  const { filters, loading, documents } = useSearchDialogContext()
  const hasNoDocuments = documents.length === 0

  const trackDocumentSelection = (
    selectedDocumentPosition: number,
    totalDocuments: number
  ) => {
    trackSelectDocumentInDialog({
      numberOfResults: totalDocuments,
      numberOfWords: filters.searchTerm.split(" ").length,
      // since array position could be 0, we want to display 1 as it's more user friendly
      searchPosition: selectedDocumentPosition + 1,
      searchQuery: filters.searchTerm
    })
  }

  const navigate = useNavigate()

  const customTypes = useRepositoryTypes()
  const customTypeLabelById = useMemo(
    () =>
      keyBy(
        customTypes,
        (type) => type.id,
        (type) => type.label
      ),
    [customTypes]
  )

  // No search filters applied yet
  if (hasNoDocuments && !filters.hasFiltersApplied) {
    return (
      <NoDocumentsPlaceholder
        title="Search for pages"
        subtitle="Enter keywords or apply filters to find the pages you need."
      />
    )
  }

  if (hasNoDocuments && !loading) {
    return (
      <NoDocumentsPlaceholder
        title="No pages found"
        subtitle="Consider using different keywords or tweaking your filters to find what you need."
      />
    )
  }

  const showSearchSkeleton = hasNoDocuments && loading
  const showProgressCircle = loading && !showSearchSkeleton

  return (
    <ScrollArea style={{ paddingLeft: 12, paddingRight: 1 }}>
      <Box padding={{ top: 10, bottom: 4 }} gap={8}>
        <Text variant="smallBold" color="grey11">
          Pages
        </Text>
        {showProgressCircle && <ProgressCircle color="grey11" />}
      </Box>

      {showSearchSkeleton ? (
        <SearchSkeletonLoader />
      ) : (
        documents.map((document, index) => (
          <Link
            onClick={() => trackDocumentSelection(index, documents.length)}
            to={`/pages/${document.id}`}
            key={document.id}
            style={{ textDecoration: "none" }}
          >
            <ComboBoxItem
              value={document.id}
              onCheckedChange={() => {
                navigate(`/pages/${document.id}`)
              }}
              variant="action-list-item"
            >
              <Icon
                name="description"
                size="small"
                color="grey11"
                sx={{ marginRight: 8 }}
              />
              <Text>
                <HighlightedText
                  text={document.title}
                  highlight={filters.searchTerm}
                />
                <CustomTypeName
                  label={customTypeLabelById.get(document.custom_type_id)}
                />
              </Text>
            </ComboBoxItem>
          </Link>
        ))
      )}
    </ScrollArea>
  )
}

interface ActionBarProps {
  selectedFilters: Filter[]
  setSelectedFilters: Dispatch<SetStateAction<Filter[]>>
}

function ActionBar(props: ActionBarProps) {
  const { selectedFilters, setSelectedFilters } = props

  const { filters, tagFilterDropdownRef, typeFilterDropdownRef } =
    useSearchDialogContext()

  function onRemoveFilter(filterId: string, filterType: FilterType) {
    const newValues = selectedFilters?.filter(
      (filter) => filter.value !== filterId || filter.type !== filterType
    )

    setSelectedFilters(newValues)
  }

  function onFilterSelected(newFilterId: string, filterType: FilterType) {
    const alreadySelected = selectedFilters?.some(
      (type) => type.value === newFilterId && type.type === filterType
    )

    if (alreadySelected) return onRemoveFilter(newFilterId, filterType)

    const newFilters = [
      ...selectedFilters,
      { value: newFilterId, type: filterType }
    ]

    setSelectedFilters(newFilters)
  }

  return (
    <Box flexDirection="column" gap={8} padding={{ inline: 12, block: 8 }}>
      <Box alignItems="center" gap={16}>
        <FilterDropDownBoundary
          type="customType"
          selectedFilters={filters.customType}
        >
          <FilterDropDown
            type="customType"
            optionsFetcher={useRepositoryTypesFilters}
            selectedFilters={filters.customType}
            onFilterSelected={onFilterSelected}
            ref={typeFilterDropdownRef}
          />
        </FilterDropDownBoundary>
        <FilterDropDownBoundary type="tag" selectedFilters={filters.tag}>
          <FilterDropDown
            type="tag"
            optionsFetcher={useRepositoryTagsFilters}
            selectedFilters={filters.tag}
            onFilterSelected={onFilterSelected}
            ref={tagFilterDropdownRef}
          />
        </FilterDropDownBoundary>
      </Box>

      {selectedFilters.length > 0 && (
        <FiltersBar
          selectedFilters={selectedFilters}
          onRemoveFilter={onRemoveFilter}
        />
      )}
    </Box>
  )
}

function SearchSkeletonLoader() {
  return (
    <Box padding={{ inline: 12, top: 12 }} flexDirection="column" gap={24}>
      {range(1, 8).map((num) => (
        <Box display="flex" alignItems="center" gap={12} key={num}>
          <Icon name="description" size="small" color="grey11" />
          <Skeleton
            key={num}
            height={16}
            sx={{ flexGrow: 1 }}
            borderRadius={12}
          />
        </Box>
      ))}
    </Box>
  )
}

interface NoDocumentsPlaceholderProps {
  title: string
  subtitle?: string
}

function NoDocumentsPlaceholder(props: NoDocumentsPlaceholderProps) {
  const { title, subtitle } = props

  return (
    <BlankSlate sx={{ marginTop: 72 }}>
      <BlankSlateIcon name="search" backgroundColor="grey4" size="large" />
      <BlankSlateTitle size="big">{title}</BlankSlateTitle>
      <BlankSlateDescription width={420}>{subtitle}</BlankSlateDescription>
    </BlankSlate>
  )
}

interface HighlightedTextProps {
  text: string
  highlight: string
}

function HighlightedText(props: HighlightedTextProps) {
  const { text, highlight } = props
  const parts = text.split(new RegExp(`(${highlight})`, "gi"))
  return (
    <Text variant="normal" color="grey11" hyphens overflowWrap="anywhere">
      {parts.map((part, index) =>
        part.toLowerCase() === highlight.toLowerCase() ? (
          <Text variant="bold" key={index} hyphens overflowWrap="anywhere">
            {part}
          </Text>
        ) : (
          part
        )
      )}
    </Text>
  )
}

interface CustomTypeNameProps {
  label?: string
}

function CustomTypeName(props: CustomTypeNameProps) {
  const { label } = props

  if (!label) return null

  return (
    <Text variant="small" color="grey11" hyphens overflowWrap="anywhere">
      {` — ${label}`}
    </Text>
  )
}
